import React from 'react';
import { m } from 'framer-motion';

interface LanguageToggleProps {
  language: 'en' | 'es';
  onChange: (language: 'en' | 'es') => void;
  disabled?: boolean;
  className?: string;
}

const LanguageToggle: React.FC<LanguageToggleProps> = ({
  language,
  onChange,
  disabled = false,
  className = '',
}) => {
  const toggleLanguage = () => {
    if (!disabled) {
      onChange(language === 'en' ? 'es' : 'en');
    }
  };

  const handleToggleKeyDown = (e: React.KeyboardEvent) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault();
      onChange(language === 'en' ? 'es' : 'en');
    }
  };

  return (
    <button
      type="button"
      onClick={toggleLanguage}
      onKeyDown={handleToggleKeyDown}
      disabled={disabled}
      className={`
        relative inline-flex items-center rounded-lg p-1 transition-all duration-200 ease-in-out
        focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2
        dark:focus:ring-offset-zinc-900
        bg-zinc-100 dark:bg-zinc-700 hover:bg-zinc-200 dark:hover:bg-zinc-600
        hover:shadow-sm active:scale-95
        ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}
        ${className}
      `}
      style={{ width: '64px', height: '32px' }}
    >
      {/* Background sliding indicator */}
      <m.div
        className="absolute top-1 bottom-1 left-1 rounded-md bg-white dark:bg-zinc-800 shadow-sm"
        animate={{
          x: language === 'es' ? '100%' : '0%',
        }}
        transition={{
          type: 'spring',
          stiffness: 500,
          damping: 30,
        }}
        style={{ width: 'calc(50% - 4px)' }}
      />
      
      {/* EN Label */}
      <div className="relative z-10 flex-1 flex items-center justify-center">
        <m.span
          className={`text-xs font-medium transition-colors duration-200 ${
            language === 'en'
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-zinc-500 dark:text-zinc-400'
          }`}
          animate={{
            scale: language === 'en' ? 1.05 : 1,
          }}
          transition={{ duration: 0.2 }}
        >
          EN
        </m.span>
      </div>
      
      {/* ES Label */}
      <div className="relative z-10 flex-1 flex items-center justify-center">
        <m.span
          className={`text-xs font-medium transition-colors duration-200 ${
            language === 'es'
              ? 'text-primary-600 dark:text-primary-400'
              : 'text-zinc-500 dark:text-zinc-400'
          }`}
          animate={{
            scale: language === 'es' ? 1.05 : 1,
          }}
          transition={{ duration: 0.2 }}
        >
          ES
        </m.span>
      </div>
    </button>
  );
};

export default LanguageToggle;
